import { useEffect, useState } from 'react';
import { finApi } from '../../api/finance.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function Banking() {
  const { can } = useAuth();
  const canCreate = can('bank_account', 'create');
  const canReconcile = can('bank_transaction', 'reconcile');

  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [matches, setMatches] = useState({}); // bankTransactionId -> paymentId
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  function refresh() {
    finApi.bankAccounts().then((d) => setAccounts(d?.items || []));
    finApi.bankTransactions({ unmatched: 'true' }).then((d) => setTransactions(d?.items || []));
    finApi.payments().then((d) => setPayments(d?.items || []));
  }
  useEffect(refresh, []);

  async function newAccount(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await finApi.createBankAccount({
        bankName: fd.get('bankName'),
        accountNo: fd.get('accountNo'),
        accountName: fd.get('accountName'),
        currency: fd.get('currency') || 'USD',
        currentBalance: Number(fd.get('currentBalance') || 0),
      });
      e.currentTarget.reset();
      setShowForm(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  async function importTxns(bankAccountId) {
    const text = window.prompt('Paste CSV: date,description,amount  (one per line)');
    if (!text) return;
    const items = text.split(/\r?\n/).filter(Boolean).map((line) => {
      const [date, description, amount] = line.split(',').map((s) => s?.trim());
      return { date, description, amount: Number(amount) };
    });
    try {
      await finApi.importBankTransactions(bankAccountId, items);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Import failed');
    }
  }

  async function reconcile() {
    const matchList = Object.entries(matches).filter(([, p]) => !!p).map(([bankTransactionId, paymentId]) => ({ bankTransactionId, paymentId }));
    if (!matchList.length) return;
    setError(null);
    try {
      const res = await finApi.reconcile(matchList);
      setInfo(`Matched ${res.matched} transaction(s)`);
      setMatches({});
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Reconciliation failed');
    }
  }

  return (
    <section>
      <header className="page-header">
        <h1>Banking</h1>
        {canCreate && <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New Account'}</button>}
      </header>

      {error && <div className="alert error">{error}</div>}
      {info && <div className="alert info">{info}</div>}

      {showForm && (
        <form onSubmit={newAccount} className="card form-card grid-form">
          <label>Bank Name<input name="bankName" required /></label>
          <label>Account Name<input name="accountName" required /></label>
          <label>Account #<input name="accountNo" required /></label>
          <label>Currency<input name="currency" defaultValue="USD" /></label>
          <label>Opening Balance<input name="currentBalance" type="number" step="0.01" defaultValue={0} /></label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}

      <div className="card">
        <h3>Bank Accounts</h3>
        <table className="data-table">
          <thead><tr><th>Bank</th><th>Account #</th><th>Currency</th><th>Balance</th><th></th></tr></thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id}>
                <td><strong>{a.bank_name}</strong> · {a.account_name}</td>
                <td><code>{a.account_no}</code></td>
                <td>{a.currency}</td>
                <td>{Number(a.current_balance).toFixed(2)}</td>
                <td><button className="btn ghost small" onClick={() => importTxns(a.id)}>Import CSV</button></td>
              </tr>
            ))}
            {accounts.length === 0 && <tr><td colSpan={5} className="muted">No bank accounts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <header className="page-header">
          <h3>Reconciliation</h3>
          {canReconcile && Object.values(matches).some(Boolean) && (
            <button className="btn primary" onClick={reconcile}>Confirm Matches</button>
          )}
        </header>
        <p className="muted">Match each unmatched bank transaction to a recorded payment of equal amount.</p>
        <table className="data-table">
          <thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Match to Payment</th></tr></thead>
          <tbody>
            {transactions.map((t) => {
              const candidates = payments.filter((p) => Math.abs(Math.abs(Number(t.amount)) - Number(p.amount)) < 0.01);
              return (
                <tr key={t.id}>
                  <td>{t.date}</td>
                  <td>{t.description}</td>
                  <td className={Number(t.amount) >= 0 ? '' : 'danger'}>{Number(t.amount).toFixed(2)}</td>
                  <td>
                    <select value={matches[t.id] || ''} onChange={(e) => setMatches((m) => ({ ...m, [t.id]: e.target.value }))}>
                      <option value="">—</option>
                      {candidates.map((p) => <option key={p.id} value={p.id}>{p.payment_no} ({p.type})</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
            {transactions.length === 0 && <tr><td colSpan={4} className="muted">No unmatched bank transactions.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
